import { configureStore, combineReducers } from '@reduxjs/toolkit';
import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from 'redux-persist';
// ``redux-persist/lib/storage`` resolves to ``localStorage`` in the browser.
// Using localStorage (rather than the previous sessionStorage) means the
// auth session is shared across all tabs from the same origin — so
// ``window.open`` deep-links (e.g. the newsletter SOV pencil opening the
// Brand SOV editor in a new tab) inherit the same Bearer token without
// any postMessage / cookie dance. The trade-off is that auth survives
// browser-window closures; logout / 401 / explicit ``clearAuth`` still
// purge it cleanly via redux-persist.
import storage from 'redux-persist/lib/storage';
import authReducer from './slices/authSlice';
import appReducer from './slices/appSlice';
import notificationReducer from './slices/notificationSlice';

const rootReducer = combineReducers({
  auth: authReducer,
  app: appReducer,
  notifications: notificationReducer,
});

const persistConfig = {
  key: 'pr-gen-session',
  storage,
  whitelist: ['auth', 'app'],
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
  devTools: process.env.NODE_ENV !== 'production',
});

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof rootReducer>;
export type AppDispatch = typeof store.dispatch;
